import { useState, useCallback, useEffect } from 'react';

/**
 * Dual-handle range slider component
 *
 * @param {Object} props
 * @param {string} props.label - Label for the slider
 * @param {number} props.min - Minimum value
 * @param {number} props.max - Maximum value
 * @param {Array<number>} props.value - Current [min, max] values
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.formatLabel - Optional formatter for display labels
 * @param {number} props.step - Step size (default 1)
 */
export function RangeSlider({
  label,
  min,
  max,
  value,
  onChange,
  formatLabel,
  step = 1
}) {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Sync with prop changes
  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  // Debounced onChange
  useEffect(() => {
    if (isDragging) return;

    const timer = setTimeout(() => {
      if (localValue[0] !== value[0] || localValue[1] !== value[1]) {
        onChange(localValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localValue, onChange, isDragging, value]);

  const handleChange = useCallback((index, newVal) => {
    const numVal = Number(newVal);
    const updated = [...localValue];
    updated[index] = numVal;

    // Ensure min <= max
    if (index === 0 && updated[0] > updated[1]) {
      updated[0] = updated[1];
    }
    if (index === 1 && updated[1] < updated[0]) {
      updated[1] = updated[0];
    }

    setLocalValue(updated);
  }, [localValue]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  const formatter = formatLabel || ((v) => v);

  // Calculate percentage positions
  const minPercent = ((localValue[0] - min) / (max - min)) * 100;
  const maxPercent = ((localValue[1] - min) / (max - min)) * 100;

  return (
    <div className="space-y-1.5 sm:space-y-2">
      <label className="text-xs sm:text-sm font-medium text-slate-300">{label}</label>

      <div className="relative pt-8 sm:pt-6 pb-4">
        {/* Track - larger on mobile for easier touch */}
        <div className="absolute top-10 sm:top-8 left-0 right-0 h-1.5 sm:h-1 bg-slate-700 rounded" />

        {/* Active range */}
        <div
          className="absolute top-10 sm:top-8 h-1.5 sm:h-1 bg-accent-blue rounded transition-all"
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`
          }}
        />

        {/* Min thumb - larger touch target on mobile */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          onChange={(e) => handleChange(0, e.target.value)}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          className="absolute top-7 sm:top-6 w-full pointer-events-auto appearance-none bg-transparent cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6
                     sm:[&::-webkit-slider-thumb]:w-4 sm:[&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                     [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent-blue
                     [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6
                     sm:[&::-moz-range-thumb]:w-4 sm:[&::-moz-range-thumb]:h-4
                     [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                     [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent-blue
                     [&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:border-none"
          aria-label={`${label} minimum`}
        />

        {/* Max thumb - larger touch target on mobile */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          onChange={(e) => handleChange(1, e.target.value)}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          className="absolute top-7 sm:top-6 w-full pointer-events-auto appearance-none bg-transparent cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6
                     sm:[&::-webkit-slider-thumb]:w-4 sm:[&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                     [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent-blue
                     [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6
                     sm:[&::-moz-range-thumb]:w-4 sm:[&::-moz-range-thumb]:h-4
                     [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                     [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent-blue
                     [&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:border-none"
          aria-label={`${label} maximum`}
        />

        {/* Labels - clearer on mobile */}
        <div className="flex justify-between text-[11px] sm:text-xs text-slate-300 sm:text-slate-400 mt-10 sm:mt-8 font-medium sm:font-normal">
          <span className="bg-slate-800/80 px-1.5 py-0.5 rounded sm:bg-transparent sm:p-0">{formatter(localValue[0])}</span>
          <span className="bg-slate-800/80 px-1.5 py-0.5 rounded sm:bg-transparent sm:p-0">{formatter(localValue[1])}</span>
        </div>
      </div>
    </div>
  );
}
